﻿import { FIQAssist } from "../../app";
import { Inject, Injectable } from "@fiqas/angular-utils";
import { FiqassistApiServiceBase } from "./fiqassistApiServiceBase";
import { ValidationResult } from "../../models/base";
import { IIdentityLink, IIdentityResolver, IIdentifiableCollectionPropertyOf, CollectionResult } from "../../interfaces/iFqs"
import { SearchInfo, SearchData, ProblemData, CompactServiceCase, ServiceCase, ServiceCaseData, RequestData, CorrespondenceData, Correspondence, CaseType } from "../../models/serviceCase";
import { ComplaintData } from "../../models/complaint";
import { ComplimentData } from "../../models/compliment";
import { CaseFileData } from "../../models/file";
import { AuthenticationService } from "../authenticationService";
import { HomeService } from "./homeService";
import { AuthorizationService } from "../authorizationService";
import { FileService } from "./FileService";
import { LogService } from "../logService";

@Injectable({
    module: FIQAssist,
    selector: ServiceCaseService.selector
})
export class ServiceCaseService extends FiqassistApiServiceBase {
    public static selector = "ServiceCaseService"

    private maxLimit: number = 100;
    private exportLimit: number = 500;

    constructor(
        @Inject('$q') $q: angular.IQService,
        @Inject('$http') $http: angular.IHttpService,
        @Inject() private homeService: HomeService,
        @Inject() authenticationService: AuthenticationService,
        @Inject() authorizationService: AuthorizationService,
        @Inject() protected fileService: FileService,
        @Inject() protected logService: LogService
    ) {
        super($q, $http, authenticationService, authorizationService);
    }

    public async SearchInfo(customers: number[]) {
        return this.PreformSearch({ cid: customers });
    }
    public async AdvancedSearch(data: SearchData, customers: number[], limit: number = null, skip: number = null) {
        return this.PreformSearch({ cid: customers, limit: (limit > this.maxLimit) ? this.maxLimit : limit, skip: skip }, data);
    }
    public async FilteredSearch(search: string, customers: number[], limit: number = null, skip: number = null, data: SearchData = null) {
        return this.PreformSearch({ q: search, cid: customers, limit: (limit > this.maxLimit) ? this.maxLimit : limit, skip: skip }, data);
    }
    public async AdvancedExport(data: SearchData = null, customers: number[]) {
        return this.PreformSearch({ cid: customers, limit: this.exportLimit, skip: 0 }, data);
    }
    public async FilteredExport(search: string, customers: number[], data: SearchData = null) {
        return this.PreformSearch({ q: search, cid: customers, limit: this.exportLimit, skip: 0 }, data);
    }
    private async PreformSearch(params?: { [key: string]: any;}, body?: SearchData) {
        var res = await this.homeService.StartResult;
        return this.performRequest<SearchData, CollectionResult<CompactServiceCase>>("POST", res.searchCases.fullHref.replace(/{.*}/, ""), params, body);
    }

    public async NewProblem(problem: ProblemData) {
        var res = await this.homeService.StartResult;
        var response = await this.performRequestWithFullResponse<ProblemData, ValidationResult>("POST", res.newProblem.fullHref, {}, problem, { "Content-Type": "application/json; charset=utf-8" });
        if (response.status != 201)
            throw `ServiceCaseService.NewProblem() Expected 201, received ${response.status}`;

        var newId = response.headers("Location");
        return <IIdentityResolver<ProblemData>>this.createIdentityLink({ href: newId });
    }


    public async UpdateProblem(id: IIdentityLink, problem: ProblemData) {
        return this.UpdateCase<ProblemData>(id, problem);
    }

    public async UpdateCase<C extends ServiceCaseData>(id: IIdentityLink, data: C) {
        let fullHref = id.fullHref;
        if (data.type === "bug") {
            fullHref = fullHref.replace('request', 'problem');
        } else if (data.type === "request") {
            fullHref = fullHref.replace('problem', 'request');
        }
        var response = await this.performRequestWithFullResponse<C, ValidationResult>("PUT", fullHref, {}, this.sanitizeCase(data), { "Content-Type": "application/json; charset=utf-8" });
        if (response.status != 204)
            throw `ServiceCaseService.UpdateCase() Expected 204, received ${response.status}`;
    }
    // remove image src if it was added by FE.
    private sanitizeCase<C extends ServiceCaseData>(serviceCase: C) {
        // Spread work aounded. Spreading of generic is fixed in TypeScript Version 3.2.
        let sanatizeServiceCase = { ...(serviceCase as object) } as C;
        // find all images
        let images = sanatizeServiceCase.description.match(/<img [^<>]*>/g) || [];
        images.forEach(fqsImgWithSrc => {
            // find the 2 required attributes and src, that makes 3 (src shouldn't be filled for storage, except newly added images, which won't have an data-fileid)
            let attrs = fqsImgWithSrc.match(/((src|data-fileid|data-filename)="[^<>"]*")/g) || [];
            // let assume the image attributes are alway uniquely named for ease and preformance.
            if (attrs.length == 3) {
                let santizedFqsImg = fqsImgWithSrc.replace(/\ src="[^<>"]*"/g, "");
                let restoredBeSrcFqsImg = santizedFqsImg.replace(/\ srcFromBe="([^<>"]*)"/g, " src=\"$1\"");
                // only change the found image so no other changes will be made to description
                sanatizeServiceCase.description = sanatizeServiceCase.description.replace(fqsImgWithSrc, restoredBeSrcFqsImg);
            }
        });
        return sanatizeServiceCase;
    }

    public async NewRequest(request: RequestData) {
        var res = await this.homeService.StartResult;
        var response = await this.performRequestWithFullResponse<RequestData, ValidationResult>("POST", res.newRequest.fullHref, {}, request, { "Content-Type": "application/json; charset=utf-8" });
        if (response.status != 201)
            throw `ServiceCaseService.NewProblem() Expected 201, received ${response.status}`;

        var newId = response.headers("Location");
        return <IIdentityResolver<RequestData>>this.createIdentityLink({ href: newId });
    }

    public async UpdateRequest(id: IIdentityLink, request: RequestData) {
        return this.UpdateCase<RequestData>(id, request);
    }

    public async NewComplaint(cid: string, complaint: ComplaintData) {
        var res = await this.homeService.StartResult;
        var response = await this.performRequestWithFullResponse<ComplaintData, ValidationResult>("POST", res.addComplaint.fullHref.replace(/\{cid\}/, cid), {}, complaint, { "Content-Type": "application/json; charset=utf-8" })
        if (response.status != 204)
            throw `ServiceCaseService.NewComplaint() Expected 204, received ${response.status}`;
    }

    public async NewCompliment(cid: string, compliment: ComplimentData) {
        var res = await this.homeService.StartResult;
        var response = await this.performRequestWithFullResponse<ComplimentData, ValidationResult>("POST", res.addCompliment.fullHref.replace(/\{cid\}/, cid), {}, compliment, { "Content-Type": "application/json; charset=utf-8" });
        if (response.status != 204)
            throw `ServiceCaseService.NewCompliment() Expected 204, received ${response.status}`;
    }

    public GetById<T extends ServiceCaseData>(id: IIdentityLink, resolveOnReject: boolean = false) {
        return this.performRequest<void, T>("GET", id.fullHref, null, null, null, resolveOnReject).then(sc => { return this.fillImageSources(sc); });
    }
    public async GetByNumber(cid: number, resolveOnReject: boolean = false) {
        var res = await this.homeService.StartResult;
        var id = res.caseByNumber.fullHref.replace("{number}", cid + "");
        return this.performRequest<void, ServiceCase>("GET", id, null, null, null, resolveOnReject).then(sc => { return this.fillImageSources(sc); });
    }
    // load images and apply as base64 data.
    public fillImageSources<T extends ServiceCaseData>(serviceCase: T) {
        let imageFilePromises: ng.IPromise<void>[] = [];
        let imageData: { [key: string]: string } = {};

        // find all images
        let images = serviceCase.description.match(/<img [^<>]*>/g) || [];
        images.forEach(fqsImgWithSrc => {
            // find the 2 required attributes
            let attrs = fqsImgWithSrc.match(/(data-fileid|data-filename)="([^<>"]*)"/g) || [];
            // let assume the image attributes are alway uniquely named for ease and preformance.
            if (attrs.length === 2) {
                // probally unneeded as it should not be there, but remove src from img any ways if browser deside to add src themself
                let santizedFqsImg = fqsImgWithSrc.replace(/\ src="([^<>"]*)"/g, " srcFromBe=\"$1\"");
                // only change the found image so no other changes will be made to description
                serviceCase.description = serviceCase.description.replace(fqsImgWithSrc, santizedFqsImg);
                // find file id & name
                let dataFileIdMatch = fqsImgWithSrc.match(/\ data-fileid="([^<>"]*)"/i) || [];
                let dataFileNameMatch = fqsImgWithSrc.match(/\ data-filename="([^<>"]*)"/i) || [];
                if (dataFileIdMatch.length === 2 && dataFileNameMatch.length === 2) {
                    let dataFileid = dataFileIdMatch[1];
                    let dataFileName = dataFileNameMatch[1];
                    // create promise(s) from id and put it in imageData; filename keyed.
                    let promise = this.fileService.GetFileFromUrl(dataFileid).then(file => { imageData[dataFileName] = this.fileService.GenerateImageSource(file); }, error => { imageData[dataFileName] = this.fileService.ImageNotFoundSource(); this.logService.error(`fillImageSources`, `image promise failed`, error); })
                    imageFilePromises.push(promise);
                }
            }
        });
        return this.$q.all(imageFilePromises).then(_ => {
            Object.keys(imageData).forEach(dataFileName => {
                // apply images based on filename.
                serviceCase.description = serviceCase.description.replace(` data-filename="${dataFileName}"`, ` data-filename="${dataFileName}" src="${imageData[dataFileName]}"`);
            });
            return serviceCase;
        }) as angular.IPromise<T>;
    }


    public async AddCorrespondence(property: IIdentifiableCollectionPropertyOf<CorrespondenceData>, correspondence: CorrespondenceData) {
        return this.AddIIdentifiableCollectionPropertyOf<CorrespondenceData>(property, correspondence, null);
    }

    public async UpdateCorrespondence(correspondence: Correspondence) {
        return this.performRequest<CorrespondenceData, void>("PUT", correspondence.id.fullHref, null, correspondence);
    }

    public async ActiveCases(customers: number[], limit: number, mycases?: boolean) {
        var res = await this.homeService.StartResult;
        return this.performRequest<void, CollectionResult<CompactServiceCase>>("GET", res.activeCases.fullHref.replace(/{.*}/, ""), { cid: customers, limit: (limit > this.maxLimit) ? this.maxLimit : limit, forActiveUser: !!mycases });
    }

    public async ActiveCasesExport(customers: number[], mycases?: boolean) {
        var res = await this.homeService.StartResult;
        return this.performRequest<void, number>("GET", res.activeCases.fullHref.replace(/{.*}/, ""), { cid: customers, limit: this.exportLimit, skip: 0, forActiveUser: !!mycases });
    }

    public async ActiveCasesCount(customers: number[], mycases?: boolean) {
        var res = await this.homeService.StartResult;
        return this.performRequest<void, number>("GET", res.activeCasesCount.fullHref.replace(/{.*}/, ""), { cid: customers, forActiveUser: !!mycases });
    }

    public async ActionRequired(customers: number[], limit: number, mycases?: boolean) {
        var res = await this.homeService.StartResult;
        return this.performRequest<void, CollectionResult<CompactServiceCase>>("GET", res.actionRequiredCases.fullHref.replace(/{.*}/, ""), { cid: customers, limit: (limit > this.maxLimit) ? this.maxLimit : limit, forActiveUser: !!mycases });
    }

    public async ActionRequiredExport(customers: number[], mycases?: boolean) {
        var res = await this.homeService.StartResult;
        return this.performRequest<void, number>("GET", res.actionRequiredCases.fullHref.replace(/{.*}/, ""), { cid: customers, limit: this.exportLimit, skip: 0, forActiveUser: !!mycases });
    }

    public async ActionRequiredCount(customers: number[], mycases?: boolean) {
        var res = await this.homeService.StartResult;
        return this.performRequest<void, number>("GET", res.actionRequiredCasesCount.fullHref.replace(/{.*}/, ""), { cid: customers, forActiveUser: !!mycases });
    }

    public async AwaitingFeedback(customers: number[], limit: number, mycases?: boolean) {
        var res = await this.homeService.StartResult;
        return this.performRequest<void, CollectionResult<CompactServiceCase>>("GET", res.awaitingFeedbackCases.fullHref.replace(/{.*}/, ""), { cid: customers, limit: (limit > this.maxLimit) ? this.maxLimit : limit, forActiveUser: !!mycases });
    }

    public async AwaitingFeedbackExport(customers: number[], mycases?: boolean) {
        var res = await this.homeService.StartResult;
        return this.performRequest<void, number>("GET", res.awaitingFeedbackCases.fullHref.replace(/{.*}/, ""), { cid: customers, limit: this.exportLimit, skip: 0, forActiveUser: !!mycases });
    }

    public async AwaitingFeedbackCount(customers: number[], mycases?: boolean) {
        var res = await this.homeService.StartResult;
        return this.performRequest<void, number>("GET", res.awaitingFeedbackCasesCount.fullHref.replace(/{.*}/, ""), { cid: customers, forActiveUser: !!mycases });
    }

    public async AddFile(property: IIdentifiableCollectionPropertyOf<CaseFileData>, file: CaseFileData) {
        return this.AddIIdentifiableCollectionPropertyOf<CaseFileData>(property, file, null);
    }

    public async DeleteFile(id: IIdentityLink) {
        return this.performRequest<void, void>("DELETE", id.fullHref);
    }
}
